import React, { useState } from 'react';
import { Button } from 'src/components/ui/Button';
import { HexColorPicker } from 'react-colorful';

interface CustomizeThemeModalProps {
  onClose: () => void;
  onSubmit: (data: {
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
  }) => Promise<void>;
  initialData?: {
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
  };
}

export const CustomizeThemeModal: React.FC<CustomizeThemeModalProps> = ({
  onClose,
  onSubmit,
  initialData,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    primaryColor: initialData?.primaryColor || '#6366f1',
    secondaryColor: initialData?.secondaryColor || '#8b5cf6',
    accentColor: initialData?.accentColor || '#ec4899'
  });

  const handleSubmit = async () => {
    setModalError(null);
    setIsSubmitting(true);

    try {
      await onSubmit({
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor,
        accentColor: formData.accentColor
      });
      onClose();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to update theme');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setModalError(null);
  };

  const hasChanges = () => {
    return (
      formData.primaryColor !== (initialData?.primaryColor || '#6366f1') ||
      formData.secondaryColor !== (initialData?.secondaryColor || '#8b5cf6') ||
      formData.accentColor !== (initialData?.accentColor || '#ec4899')
    );
  };

  const presetColors = [
    { name: 'Indigo', primary: '#6366f1', secondary: '#8b5cf6', accent: '#ec4899' },
    { name: 'Blue', primary: '#3b82f6', secondary: '#06b6d4', accent: '#10b981' },
    { name: 'Purple', primary: '#a855f7', secondary: '#ec4899', accent: '#f59e0b' },
    { name: 'Green', primary: '#10b981', secondary: '#14b8a6', accent: '#06b6d4' },
    { name: 'Orange', primary: '#f97316', secondary: '#eab308', accent: '#ef4444' },
    { name: 'Red', primary: '#ef4444', secondary: '#f59e0b', accent: '#ec4899' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-neutral-900 border border-white/10 rounded-xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-white text-xl font-semibold">Customize Theme</h2>
          <button onClick={handleClose} className="text-white/40 hover:text-white">✕</button>
        </div>

        {modalError && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {modalError}
          </div>
        )}

        <div className="space-y-6">
          {/* Preview */}
          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <label className="block text-white text-sm font-medium mb-3">Preview</label>
            <div className="flex gap-2">
              <div
                className="flex-[2] h-20 rounded-lg"
                style={{
                  background: `linear-gradient(135deg, ${formData.primaryColor}, ${formData.secondaryColor})`
                }}
              />
              <div
                className="flex-1 h-20 rounded-lg"
                style={{
                  backgroundColor: formData.accentColor
                }}
              />
            </div>
          </div>

          {/* Color Pickers */}
          <div className="space-y-4">
            <div>
              <label className="block text-white text-sm font-medium mb-2">Primary Color</label>
              <div className="space-y-2">
                <HexColorPicker
                  color={formData.primaryColor}
                  onChange={(color: string) => setFormData(prev => ({ ...prev, primaryColor: color }))}
                  style={{ width: '100%', height: '150px' }}
                />
                <input
                  type="text"
                  value={formData.primaryColor}
                  onChange={(e) => setFormData(prev => ({ ...prev, primaryColor: e.target.value }))}
                  placeholder="#6366f1"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">Secondary Color</label>
              <div className="space-y-2">
                <HexColorPicker
                  color={formData.secondaryColor}
                  onChange={(color: string) => setFormData(prev => ({ ...prev, secondaryColor: color }))}
                  style={{ width: '100%', height: '150px' }}
                />
                <input
                  type="text"
                  value={formData.secondaryColor}
                  onChange={(e) => setFormData(prev => ({ ...prev, secondaryColor: e.target.value }))}
                  placeholder="#8b5cf6"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">Accent Color</label>
              <div className="space-y-2">
                <HexColorPicker
                  color={formData.accentColor}
                  onChange={(color: string) => setFormData(prev => ({ ...prev, accentColor: color }))}
                  style={{ width: '100%', height: '150px' }}
                />
                <input
                  type="text"
                  value={formData.accentColor}
                  onChange={(e) => setFormData(prev => ({ ...prev, accentColor: e.target.value }))}
                  placeholder="#ec4899"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 font-mono"
                />
              </div>
            </div>
          </div>

          {/* Presets */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">Color Presets</label>
            <div className="grid grid-cols-3 gap-2">
              {presetColors.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => setFormData({
                    primaryColor: preset.primary,
                    secondaryColor: preset.secondary,
                    accentColor: preset.accent
                  })}
                  className="p-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <div className="flex gap-1 h-8 rounded mb-1 overflow-hidden">
                    <div
                      className="flex-[2]"
                      style={{
                        background: `linear-gradient(135deg, ${preset.primary}, ${preset.secondary})`
                      }}
                    />
                    <div
                      className="flex-1"
                      style={{ backgroundColor: preset.accent }}
                    />
                  </div>
                  <div className="text-white/60 text-xs text-center">{preset.name}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={handleClose} variant="ghost" className="flex-1">Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="default"
            className="flex-1"
            disabled={!hasChanges() || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Theme'}
          </Button>
        </div>
      </div>
    </div>
  );
};